import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { getSchema } from "../data/schemaData";
import {
  ArrowLeft, Printer, Dumbbell, Clock, Info,
  Award, Target, LogOut, X, CheckCircle, ClipboardList, Play
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fitsmith-academy/artifacts/zbh0gz3y_291E0B11-36FD-4420-BAF1-DDEC0FEFC105.png";
const API = process.env.REACT_APP_BACKEND_URL;

// ─── NIVEAU KLEUR ─────────────────────────────────────────────────────────────
function niveauColor(niveau) {
  if (niveau === "Advanced")  return { bg: "rgba(239,68,68,0.1)",  border: "rgba(239,68,68,0.3)",  text: "#f87171" };
  if (niveau === "Gevorderd") return { bg: "rgba(249,115,22,0.1)", border: "rgba(249,115,22,0.3)", text: "#fb923c" };
  return                             { bg: "rgba(34,197,94,0.1)",  border: "rgba(34,197,94,0.3)",  text: "#4ade80" };
}

// ─── EXERCISE ROW ─────────────────────────────────────────────────────────────
function ExerciseRow({ exercise, isFirst, isLast }) {
  const isSuperset = !!exercise.superset;
  return (
    <div
      data-testid={`exercise-${exercise.code}`}
      className={`schema-exercise-row ${isSuperset ? "schema-superset" : ""}`}
    >
      {/* Superset balk links */}
      {isSuperset && (
        <div className={`schema-superset-bar ${isFirst ? "superset-bar-top" : isLast ? "superset-bar-bottom" : "superset-bar-mid"}`} />
      )}

      {/* Code */}
      <div className="schema-ex-code">{exercise.code}</div>

      {/* Naam + notitie */}
      <div className="schema-ex-info">
        <p className="schema-ex-naam">{exercise.naam}</p>
        {exercise.notitie && (
          <p className="schema-ex-notitie">
            <Info size={10} className="inline mr-1 shrink-0" style={{ verticalAlign: "text-top" }} />
            {exercise.notitie}
          </p>
        )}
        {isSuperset && (
          <span className="schema-superset-label">Superset met {exercise.superset}</span>
        )}
      </div>

      {/* Stats */}
      <div className="schema-ex-stats">
        <div className="schema-stat-item">
          <span className="schema-stat-label">Sets/Reps</span>
          <span className="schema-stat-val">{exercise.sets_reps}</span>
        </div>
        <div className="schema-stat-item">
          <span className="schema-stat-label">Tempo</span>
          <span className="schema-stat-val font-mono">{exercise.tempo}</span>
        </div>
        <div className="schema-stat-item">
          <span className="schema-stat-label">Rust</span>
          <span className="schema-stat-val">{exercise.rust}</span>
        </div>
      </div>
    </div>
  );
}

// ─── LOG SESSION MODAL ────────────────────────────────────────────────────────
function LogSessionModal({ session, lastLog, onSave, onClose, saving }) {
  const [weights, setWeights] = useState(() => {
    const map = {};
    if (lastLog) lastLog.exercises.forEach(e => { map[e.code] = e.weight; });
    session.exercises.forEach(e => { if (!map[e.code]) map[e.code] = ""; });
    return map;
  });

  const lastDate = lastLog
    ? new Date(lastLog.logged_at).toLocaleDateString("nl-NL", { day: "numeric", month: "long" })
    : null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#111] border border-white/10 rounded-2xl p-6 animate-fadeIn max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-white font-bold text-lg">{session.naam} loggen</h2>
            {lastDate
              ? <p className="text-[#C9A84C] text-xs mt-0.5">Gewichten van {lastDate} ingevuld</p>
              : <p className="text-zinc-500 text-xs mt-0.5">Vul je gebruikte gewichten in</p>}
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        <div className="space-y-2 mb-6">
          {session.exercises.map(ex => (
            <div key={ex.code} className="flex items-center gap-3 bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-semibold truncate">{ex.naam}</p>
                <p className="text-zinc-500 text-xs">{ex.sets_reps}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <input
                  type="number" min="0" step="2.5"
                  value={weights[ex.code] || ""}
                  onChange={e => setWeights(w => ({ ...w, [ex.code]: e.target.value }))}
                  placeholder="0"
                  className="w-16 bg-[#0d0d0d] border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm text-center font-mono focus:outline-none focus:border-[#C9A84C]/40"
                />
                <span className="text-zinc-500 text-xs">kg</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="btn-course-outline flex-1 py-2.5 text-sm font-semibold">Annuleren</button>
          <button onClick={() => onSave(session.id, session.naam, session.exercises.map(ex => ({
            code: ex.code, naam: ex.naam, weight: weights[ex.code] || "0", sets_reps: ex.sets_reps
          })))} disabled={saving}
            data-testid="confirm-log-btn"
            className="btn-gold-course flex-1 py-2.5 text-sm font-bold uppercase tracking-wide disabled:opacity-40">
            {saving ? "Opslaan..." : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SESSION BLOCK ────────────────────────────────────────────────────────────
function SessionBlock({ session, index, lastLog, onLog, onStart, showLog }) {
  const exercises = session.exercises;
  const lastDate = lastLog
    ? new Date(lastLog.logged_at).toLocaleDateString("nl-NL", { day: "numeric", month: "long" })
    : null;

  return (
    <div className="schema-session-block" data-testid={`session-${session.id}`}>
      <div className="schema-session-header">
        <div className="schema-session-num">{String.fromCharCode(65 + index)}</div>
        <h2 className="schema-session-title">{session.naam}</h2>
        <span className="schema-session-count">{exercises.length} oefeningen</span>
        {showLog && (
          <div className="ml-auto flex items-center gap-2 print:hidden">
            <button
              onClick={onStart}
              data-testid={`start-workout-btn-${session.id}`}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors"
            >
              <Play size={11} fill="currentColor" /> Training starten
            </button>
            <button onClick={() => onLog(session)}
              data-testid={`log-session-btn-${session.id}`}
              className="btn-course-outline text-xs px-3 py-1.5 flex items-center gap-1.5">
              <ClipboardList size={12} /> Loggen
            </button>
          </div>
        )}
      </div>

      {/* Laatste sessie samenvatting */}
      {lastLog && (
        <div className="mx-4 mb-3 px-4 py-2.5 bg-[#C9A84C]/8 border border-[#C9A84C]/20 rounded-xl flex items-start gap-2 print:hidden">
          <CheckCircle size={13} className="text-[#C9A84C] mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-[#C9A84C] text-xs font-semibold">Laatste training: {lastDate}</p>
            <p className="text-zinc-500 text-xs mt-0.5 truncate">
              {lastLog.exercises.filter(e => e.weight && e.weight !== "0").map(e => `${e.naam.split("–")[0].trim()}: ${e.weight} kg`).join(" · ")}
            </p>
          </div>
        </div>
      )}

      <div className="schema-exercises">
        {/* Kolom headers */}
        <div className="schema-col-headers">
          <div className="schema-col-code">Code</div>
          <div className="schema-col-info">Oefening</div>
          <div className="schema-col-stats">Sets / Reps / Tempo / Rust</div>
        </div>

        {exercises.map((ex, i) => {
          const paired = exercises.find(e => e.code === ex.superset);
          const pairedIdx = paired ? exercises.indexOf(paired) : -1;
          return (
            <ExerciseRow
              key={ex.code}
              exercise={ex}
              isFirst={pairedIdx > i}
              isLast={pairedIdx < i && !!ex.superset}
            />
          );
        })}
      </div>
    </div>
  );
}

// ─── SCHEMA VIEWER ────────────────────────────────────────────────────────────
export default function SchemaViewer() {
  const { comboKey } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const baseSchema = getSchema(comboKey);

  const [overrideSessions, setOverrideSessions] = useState(null);
  const [overrideWeek2, setOverrideWeek2] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logModal, setLogModal] = useState(null);
  const [logSaving, setLogSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/schemas/${comboKey}`)
      .then(r => r.json())
      .then(d => {
        if (d.sessions?.length) setOverrideSessions(d.sessions);
        if (d.week2_sessions?.length) setOverrideWeek2(d.week2_sessions);
      })
      .catch(() => { /* schema override niet beschikbaar, gebruik standaard data */ });
  }, [comboKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user?.token) return;
    fetch(`${API}/api/schema-progress/${comboKey}`, {
      headers: { Authorization: `Bearer ${user.token}` }
    }).then(r => r.json()).then(d => setLogs(d.logs || [])).catch(() => {});
  }, [comboKey, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const getLastLog = (sessionId) => logs.find(l => l.session_id === sessionId);

  const handleSaveLog = async (sessionId, sessionNaam, exercises) => {
    setLogSaving(true);
    try {
      await fetch(`${API}/api/schema-progress/${comboKey}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({ session_id: sessionId, session_naam: sessionNaam, exercises }),
      });
      const d = await fetch(`${API}/api/schema-progress/${comboKey}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      }).then(r => r.json());
      setLogs(d.logs || []);
      setLogModal(null);
    } catch { /* log opslaan mislukt */ }
    setLogSaving(false);
  };

  const schema = baseSchema;
  const meta = schema?.meta;
  const sessions = overrideSessions || schema?.sessions || [];
  const week2_sessions = overrideWeek2 || schema?.week2_sessions;
  const niveau = meta?.niveau || "";
  const nc = niveauColor(niveau);

  if (!schema) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center gap-4">
        <Dumbbell size={40} className="text-zinc-700" />
        <p className="text-zinc-400 text-sm">Schema niet gevonden.</p>
        <button onClick={() => navigate("/account")} className="btn-course-outline text-sm px-4 py-2">
          Terug naar account
        </button>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-[#0a0a0a] print:bg-white">
      {/* Navbar */}
      <nav className="bg-[#0a0a0a] border-b border-white/5 px-4 sm:px-8 py-4 flex items-center justify-between print:hidden">
        <button onClick={() => navigate("/account")} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
          <ArrowLeft size={16} />
          <span className="text-sm">Terug naar account</span>
        </button>
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="FitSmith" className="h-7 w-7 object-contain" />
          <span className="text-white font-bold text-base tracking-wide">FitSmith</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.print()}
            data-testid="print-schema-btn"
            className="btn-course-outline text-xs px-3 py-1.5 flex items-center gap-1.5"
          >
            <Printer size={13} /> Printen
          </button>
          {user && (
            <button onClick={() => { logout(); navigate("/"); }} className="text-zinc-600 hover:text-zinc-300 transition-colors" title="Uitloggen">
              <LogOut size={16} />
            </button>
          )}
        </div>
      </nav>

      {/* Print header (alleen zichtbaar bij printen) */}
      <div className="hidden print:flex items-center gap-3 p-8 pb-4 border-b border-gray-200">
        <img src={LOGO_URL} alt="FitSmith" className="h-10 w-10 object-contain" />
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-gray-400">FitSmith Academy</p>
          <p className="text-lg font-bold text-gray-900">{meta.schema_naam}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 print:py-4 print:px-8">
        {/* Schema header */}
        <div className="mb-8 print:mb-6">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span
              className="schema-niveau-badge"
              style={{ background: nc.bg, border: `1px solid ${nc.border}`, color: nc.text }}
            >
              {niveau}
            </span>
            <span className="schema-niveau-badge" style={{ background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.25)", color: "#C9A84C" }}>
              <Target size={10} className="inline mr-1" />
              {meta.doel}
            </span>
            <span className="schema-niveau-badge" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", color: "#a1a1aa" }}>
              <Clock size={10} className="inline mr-1" />
              {meta.frequentie}
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-white print:text-gray-900 mb-1">
            {meta.schema_naam}
          </h1>
          <p className="text-zinc-500 text-sm print:text-gray-500">{meta.doel} {meta.sub_doel}</p>
        </div>

        {/* Weekstructuur */}
        <div className="schema-week-card mb-8 print:mb-6 print:border-gray-200 print:bg-gray-50">
          <p className="text-xs font-bold uppercase tracking-widest text-[#C9A84C] print:text-gray-400 mb-2">Weekstructuur</p>
          <p className="text-white print:text-gray-900 text-sm font-semibold whitespace-pre-line">{meta.weekstructuur}</p>
          {meta.notitie && (
            <p className="text-zinc-500 print:text-gray-500 text-xs mt-2 leading-relaxed">
              {meta.notitie}
            </p>
          )}
        </div>

        {/* Sessions — Week 1 */}
        <div className="mb-6">
          {sessions.length > 2 && (
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-600 mb-4">Week 1</p>
          )}
          <div className="space-y-6">
            {sessions.map((session, i) => (
              <SessionBlock key={`w1-${session.id}-${i}`} session={session} index={i}
                lastLog={getLastLog(session.id)} onLog={setLogModal}
                onStart={() => navigate(`/workout/${comboKey}/${session.id}`)}
                showLog={!!user} />
            ))}
          </div>
        </div>

        {/* Sessions — Week 2 (alleen bij 3x) */}
        {week2_sessions && (
          <div className="mb-6">
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-600 mb-4 mt-8">Week 2</p>
            <div className="space-y-6">
              {week2_sessions.map((session, i) => (
                <SessionBlock key={`w2-${session.id}-${i}`} session={session} index={i}
                  lastLog={getLastLog(`w2-${session.id}`)}
                  onLog={(s) => setLogModal({...s, id: `w2-${s.id}`})}
                  onStart={() => navigate(`/workout/${comboKey}/w2-${session.id}`)}
                  showLog={!!user} />
              ))}
            </div>
          </div>
        )}

        {/* Legenda */}
        <div className="schema-legenda mt-8 print:mt-6 print:border-gray-200">
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-600 mb-3">Legenda</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-zinc-500 print:text-gray-500">
            <p><span className="text-zinc-300 print:text-gray-700 font-semibold">Tempo 3010</span> — 3 sec zakken, 0 pauze onder, 1 sec omhoog, 0 pauze boven</p>
            <p><span className="text-zinc-300 print:text-gray-700 font-semibold">30 sec*</span> — rust tussen de twee superset-oefeningen</p>
            <p><span className="text-zinc-300 print:text-gray-700 font-semibold">Superset</span> — twee oefeningen direct na elkaar uitvoeren</p>
            <p><span className="text-zinc-300 print:text-gray-700 font-semibold">Dropset (*)</span> — na falen gewicht ~20% omlaag en direct doorgaan</p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-white/5 print:border-gray-200 flex items-center justify-between print:mt-6">
          <p className="text-zinc-700 text-xs print:text-gray-400">FitSmith Academy — fitsmith.nl</p>
          <p className="text-zinc-700 text-xs print:text-gray-400">Persoonlijk schema — niet doorsturen</p>
        </div>
      </div>
    </div>

    {/* Log Modal */}
    {logModal && (
      <LogSessionModal
        session={logModal}
        lastLog={getLastLog(logModal.id)}
        onSave={handleSaveLog}
        onClose={() => setLogModal(null)}
        saving={logSaving}
      />
    )}
  </>
  );
}
